import { AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { SearchService } from 'src/app/shared/services/search.service';

import { buildOffers, FAQS, formatOfferDateShort, GENERAL_TERMS, GeneralTerm, Offer, OfferStatus, offerStatus } from './offers-data';

/**
 * Offers & Promotions (/offers).
 *
 * Encapsulation is off because clause text is rendered with [innerHTML], which
 * emulated encapsulation would leave unstyled. Every rule is nested under
 * `.ip-offers` and every class carries an `op-` prefix, so nothing here reaches
 * the rest of the site and the theme's own .btn / .container / .accordion rules
 * do not reach in.
 */
@Component({
    selector: 'indiapicture-offers',
    templateUrl: './offers.component.html',
    styleUrls: ['./offers.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class OffersComponent implements AfterViewInit, OnDestroy {

    /** Room for the site's sticky header when scrolling to a section. */
    private static readonly SCROLL_OFFSET = 110;

    private static readonly STATUS_ORDER: Record<OfferStatus, number> = { Active: 0, Upcoming: 1, Expired: 2 };

    readonly offers: Offer[] = buildOffers().sort(
        (a, b) => OffersComponent.STATUS_ORDER[offerStatus(a)] - OffersComponent.STATUS_ORDER[offerStatus(b)]
    );
    readonly generalTerms = GENERAL_TERMS;
    readonly faqs = FAQS;

    /** Offer ids whose "View Offer Terms & Conditions" panel is open. */
    offerTermsOpen: { [offerId: string]: boolean } = {};
    /** Offer term groups that are open, keyed "offerId:index" (they start collapsed). */
    offerGroupOpen: { [key: string]: boolean } = {};
    /** General terms clauses that are open, keyed by term id. */
    termOpen: { [termId: string]: boolean } = {};
    /** FAQ items that are open, keyed by index. */
    faqOpen: { [index: number]: boolean } = {};
    /** The term the reader is on, for the contents list. */
    currentTermId = '';
    /** The banner shown full size, or null. */
    creative: Offer['creative'] | null = null;

    private observer: IntersectionObserver | null = null;
    private fragmentSub: Subscription | null = null;

    constructor(
        private host: ElementRef<HTMLElement>,
        private route: ActivatedRoute,
        private router: Router,
        private zone: NgZone
    ) { }

    ngAfterViewInit(): void {
        this.watchTerms();

        // A link straight to a clause or an offer's terms (/offers#term-5) opens it.
        this.fragmentSub = this.route.fragment.subscribe(fragment => {
            if (fragment) {
                setTimeout(() => this.goTo(fragment));
            }
        });
    }

    ngOnDestroy(): void {
        if (this.observer) {
            this.observer.disconnect();
        }
        if (this.fragmentSub) {
            this.fragmentSub.unsubscribe();
        }
    }

    // ── Offers ───────────────────────────────────────────────────────────

    status(o: Offer): OfferStatus {
        return offerStatus(o);
    }

    date(d: Date): string {
        return formatOfferDateShort(d);
    }

    /** "2026-11-05" in local time, for <time datetime>. */
    isoDate(d: Date): string {
        const pad = (n: number) => (n < 10 ? '0' : '') + n;
        return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
    }

    termsId(o: Offer): string {
        return 'offer-' + o.id + '-terms';
    }

    toggleOfferTerms(o: Offer): void {
        this.offerTermsOpen[o.id] = !this.offerTermsOpen[o.id];
    }

    isGroupOpen(o: Offer, i: number): boolean {
        return !!this.offerGroupOpen[o.id + ':' + i];
    }

    toggleGroup(o: Offer, i: number): void {
        const key = o.id + ':' + i;
        this.offerGroupOpen[key] = !this.offerGroupOpen[key];
    }

    allGroupsOpen(o: Offer): boolean {
        return o.offerTerms.every((_, i) => this.isGroupOpen(o, i));
    }

    toggleAllGroups(o: Offer): void {
        const open = !this.allGroupsOpen(o);
        o.offerTerms.forEach((_, i) => this.offerGroupOpen[o.id + ':' + i] = open);
    }

    openCreative(o: Offer): void {
        this.creative = o.creative;
    }

    closeCreative(): void {
        this.creative = null;
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.closeCreative();
    }

    /**
     * The hero search: the image listing for the keyword, the same destination
     * and shared search state the header search uses. Empty does nothing.
     */
    search(event: Event, keyword: string): void {
        event.preventDefault();
        const text = (keyword || '').trim();
        if (!text) {
            return;
        }
        const searchService = SearchService.getInstance();
        searchService.setSearch(text);
        searchService.setCategory('creative');
        this.router.navigate(['/product/search/images'], { queryParams: { searchText: text, category: 'creative' } });
    }

    // ── General terms and FAQ ────────────────────────────────────────────

    toggleTerm(id: string): void {
        this.termOpen[id] = !this.termOpen[id];
    }

    get allTermsOpen(): boolean {
        return this.generalTerms.every(t => this.termOpen[t.id]);
    }

    toggleAllTerms(): void {
        const open = !this.allTermsOpen;
        this.generalTerms.forEach(t => this.termOpen[t.id] = open);
    }

    toggleFaq(i: number): void {
        this.faqOpen[i] = !this.faqOpen[i];
    }

    // ── In-page navigation ───────────────────────────────────────────────

    /**
     * Scroll to a place on the page, first opening what it names: a general
     * clause (term-N) or an offer's terms panel (offer-<id>-terms).
     */
    goTo(id: string, event?: Event): void {
        if (event) {
            event.preventDefault();
        }
        if (this.generalTerms.some(t => t.id === id)) {
            this.termOpen[id] = true;
        }
        const offer = this.offers.find(o => this.termsId(o) === id);
        if (offer) {
            this.offerTermsOpen[offer.id] = true;
        }

        // After the opened panel has rendered, so the position is final.
        setTimeout(() => {
            const el = document.getElementById(id);
            if (el) {
                const top = el.getBoundingClientRect().top + window.pageYOffset - OffersComponent.SCROLL_OFFSET;
                window.scrollTo({ top, behavior: 'smooth' });
            }
        });
    }

    /**
     * Links inside [innerHTML] text are plain anchors, so they are handled here:
     * "#id" scrolls on this page, "/route" navigates inside the app.
     */
    onRichClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        const link = target && target.closest ? target.closest('a') as HTMLAnchorElement : null;
        const href = link ? link.getAttribute('href') : null;
        if (!href) {
            return;
        }
        if (href.charAt(0) === '#') {
            this.goTo(href.slice(1), event);
        } else if (href.charAt(0) === '/') {
            event.preventDefault();
            this.router.navigateByUrl(href);
        }
    }

    /** Highlights the clause in view in the contents list. */
    private watchTerms(): void {
        if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
            return;
        }
        this.zone.runOutsideAngular(() => {
            this.observer = new IntersectionObserver(entries => {
                const hit = entries.find(e => e.isIntersecting);
                if (hit) {
                    this.zone.run(() => this.currentTermId = (hit.target as HTMLElement).id);
                }
            }, { rootMargin: '-30% 0px -60% 0px' });

            this.host.nativeElement.querySelectorAll('.op-terms .op-acc-item').forEach(el => this.observer!.observe(el));
        });
    }

    trackByOffer(_: number, item: Offer): string {
        return item.id;
    }

    trackByTerm(_: number, item: GeneralTerm): string {
        return item.id;
    }
}
